import { useEffect, useRef, useState, type ReactNode } from "react";

interface LazyOnViewProps {
  /** Rendered eagerly inside a placeholder until the container scrolls
   *  into view. Defaults to a same-size empty div for stable layout. */
  children: ReactNode;
  /** Placeholder shown until intersection. Use to match the height of
   *  the lazy content so layout doesn't shift. */
  placeholder?: ReactNode;
  /** rootMargin for the IntersectionObserver (e.g., "200px" starts
   *  loading slightly before the element enters the viewport). */
  rootMargin?: string;
  className?: string;
}

/**
 * Renders `children` only after its container scrolls within the
 * IntersectionObserver root margin. Once rendered, stays mounted,
 * we never unmount on scroll-out (cheap to keep, avoids re-loading
 * heavy libraries like Google Maps when the user scrolls back up).
 *
 * Use to defer expensive components (maps, iframes, charts) below
 * the fold so the initial page load doesn't pay for them.
 */
const LazyOnView = ({
  children,
  placeholder,
  rootMargin = "200px",
  className,
}: LazyOnViewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    // Older browsers / SSR: just render immediately.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} className={className}>
      {visible ? children : placeholder ?? null}
    </div>
  );
};

export default LazyOnView;
